import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { useCopyFromProduction, useCopyPreview } from '@/api/hooks';
import type { LicenseChangePreview } from '@/types/api';

import { ENVIRONMENT_STYLE } from './store';
import { useEnvironment } from './EnvironmentProvider';

const ACTION_LABEL: Record<LicenseChangePreview['action'], string> = {
  created: 'neu',
  updated: 'geändert',
  deleted: 'entfernt',
};

const ACTION_COLOUR: Record<LicenseChangePreview['action'], 'success' | 'warning' | 'error'> = {
  created: 'success',
  updated: 'warning',
  deleted: 'error',
};

/**
 * Confirmation for overwriting one environment's licences with production's.
 *
 * Quotes the real numbers, fetched fresh each time it opens, rather than asking
 * "are you sure?" over a generic warning. A dialog that always says the same
 * thing is one people learn to dismiss without reading; one that says "5
 * Lizenzen werden entfernt" is a question they can actually answer.
 *
 * The deletions are called out separately because they are the part nobody
 * expects. "Synchronisieren" sounds additive, and it is not.
 */
export function CopyFromProductionDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const environment = useEnvironment();
  const style = ENVIRONMENT_STYLE[environment];
  const preview = useCopyPreview(open);
  const copy = useCopyFromProduction();

  const plan = preview.data;
  const nothingToDo = plan !== undefined && plan.changes.length === 0;

  const handleConfirm = () => {
    copy.mutate(undefined, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mit Production synchronisieren</DialogTitle>
      {(preview.isLoading || copy.isPending) && <LinearProgress />}
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning" icon={<WarningAmberOutlinedIcon />}>
            Alle Lizenzen in <strong>{style.label}</strong> werden mit dem Stand von{' '}
            <strong>Production</strong> überschrieben. Lizenzen, die es nur in{' '}
            {style.label} gibt, werden dabei <strong>entfernt</strong>.
          </Alert>

          <Typography variant="body2" color="text.secondary">
            Benutzerbezogene Ausnahmen bleiben unberührt — sie gehören zu Benutzern
            dieser Umgebung und haben in Production keine Entsprechung.
          </Typography>

          {preview.isError && (
            <Alert severity="error">{preview.error.message}</Alert>
          )}
          {copy.isError && <Alert severity="error">{copy.error.message}</Alert>}

          {/* v9 dropped system shorthand props, so wrapping goes through sx. */}
          {plan && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Chip size="small" color="success" label={`${plan.created} neu`} />
              <Chip size="small" color="warning" label={`${plan.updated} geändert`} />
              <Chip size="small" color="error" label={`${plan.deleted} entfernt`} />
            </Stack>
          )}

          {nothingToDo && (
            <Alert severity="success">
              {style.label} entspricht bereits Production. Es gibt nichts zu übertragen.
            </Alert>
          )}

          {plan && plan.missingTenants.length > 0 && (
            <Alert severity="info">
              Nicht übertragbar, weil es diese Mandanten in {style.label} nicht gibt:{' '}
              {plan.missingTenants.join(', ')}. Mandanten stammen aus SEP und werden
              hier nicht angelegt.
            </Alert>
          )}

          {plan && plan.changes.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Mandant</TableCell>
                    <TableCell>App</TableCell>
                    <TableCell>Änderung</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.changes.map((change) => (
                    <TableRow key={`${change.tenant}/${change.app}`}>
                      <TableCell>{change.tenant}</TableCell>
                      <TableCell>{change.app}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={ACTION_COLOUR[change.action]}
                          label={ACTION_LABEL[change.action]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleConfirm}
          disabled={preview.isLoading || copy.isPending || nothingToDo || preview.isError}
          sx={{ textTransform: 'none' }}
        >
          {plan
            ? `${plan.created + plan.updated + plan.deleted} Änderungen übernehmen`
            : 'Übernehmen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
